<script setup lang="ts">
/**
 * HomeSearchWizardCalendar — calendrier intégré à l'étape 3 (Dates) du parcours
 * de l'accueil (DESIGN.md `calendar-day`, EXPERIENCE.md « Calendrier intégré »).
 *
 * Composant maison plutôt que `UCalendar` : la plage de Reka intervertit arrivée
 * et départ quand le 2e clic est antérieur, alors que le parcours veut une
 * nouvelle arrivée. La logique de sélection vit dans `utils/wizard-dates.ts`.
 *
 * - `v-model` `{ start, end }` en ISO `YYYY-MM-DD` ; `today` en ISO.
 * - 1 mois au rendu serveur et < md, 2 mois ≥ md (`matchMedia`) ; toujours 1
 *   avec `singleMonth` (colonne étroite du parcours de l'accueil).
 * - Jours passés inactifs ; mois précédent bloqué au mois courant, mois suivant
 *   borné à 12 mois.
 * - Clavier (motif APG date picker) : un seul arrêt de tabulation dans les
 *   grilles (jour choisi, sinon aujourd'hui), flèches ±1/±7 jours, Début/Fin de
 *   semaine, PageUp/PageDown ±1 mois ; le mois affiché suit le focus.
 *   Entrée/Espace choisissent (vrai `<button>`).
 */
import {
  addDays,
  addMonths,
  endOfMonth,
  endOfWeek,
  formatDayLong,
  formatMonthTitle,
  monthGrid,
  nextDateSelection,
  startOfMonth,
  startOfWeek,
  weekdayNames,
  type WizardDateRange,
} from '~/utils/wizard-dates'

const props = defineProps<{
  modelValue: WizardDateRange
  /** Aujourd'hui, ISO `YYYY-MM-DD` (date locale). */
  today: string
  /** Un seul mois à toutes les largeurs (colonne trop étroite pour deux). */
  singleMonth?: boolean
}>()

const emit = defineEmits<{
  'update:modelValue': [value: WizardDateRange]
}>()

const { t, locale } = useI18n()

const MONTHS_AHEAD = 12

const rootRef = ref<HTMLElement | null>(null)

// ─── Mois affichés ───
const twoMonths = ref(false)
const monthCount = computed(() => (twoMonths.value && !props.singleMonth ? 2 : 1))

const minMonth = computed(() => startOfMonth(props.today))
const maxMonth = computed(() => addMonths(minMonth.value, MONTHS_AHEAD))
const lastSelectable = computed(() => endOfMonth(maxMonth.value))
/** Premier mois affichable au plus tard (le dernier mois visible reste ≤ maxMonth). */
const maxFirstMonth = computed(() => addMonths(maxMonth.value, -(monthCount.value - 1)))

function clampFirstMonth(month: string): string {
  if (month < minMonth.value) return minMonth.value
  if (month > maxFirstMonth.value) return maxFirstMonth.value
  return month
}

const firstMonth = ref(clampFirstMonth(startOfMonth(props.modelValue.start ?? props.today)))

const visibleMonths = computed(() =>
  Array.from({ length: monthCount.value }, (_, i) => addMonths(firstMonth.value, i)),
)
const lastVisibleDay = computed(() => endOfMonth(visibleMonths.value[visibleMonths.value.length - 1]!))

const canGoPrev = computed(() => firstMonth.value > minMonth.value)
const canGoNext = computed(() => firstMonth.value < maxFirstMonth.value)

function shiftMonth(delta: number) {
  firstMonth.value = clampFirstMonth(addMonths(firstMonth.value, delta))
}

let mediaQuery: MediaQueryList | null = null
function onMediaChange() {
  twoMonths.value = !!mediaQuery?.matches
  firstMonth.value = clampFirstMonth(firstMonth.value)
}
onMounted(() => {
  if (typeof window.matchMedia !== 'function') return
  mediaQuery = window.matchMedia('(min-width: 768px)')
  onMediaChange()
  mediaQuery.addEventListener?.('change', onMediaChange)
})
onBeforeUnmount(() => mediaQuery?.removeEventListener?.('change', onMediaChange))

// ─── Grilles ───
const currentYear = computed(() => Number(props.today.slice(0, 4)))
const weekdaysLong = computed(() => weekdayNames(locale.value, 'long'))
const weekdaysShort = computed(() => weekdayNames(locale.value, 'short'))

function isVisible(iso: string): boolean {
  return iso >= visibleMonths.value[0]! && iso <= lastVisibleDay.value
}
function isSelectable(iso: string): boolean {
  return iso >= props.today && iso <= lastSelectable.value
}

const months = computed(() =>
  visibleMonths.value.map(month => ({
    id: month,
    title: formatMonthTitle(month, locale.value),
    weeks: monthGrid(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1),
  })),
)

/** « Octobre 2026 et Novembre 2026 » — annoncé à chaque changement de mois. */
const monthsAnnouncement = computed(() =>
  months.value.map(m => m.title).join(` ${t('pages.home.wizard.dates.calendar.and')} `),
)

// ─── Focus itinérant ───
const focusedDate = ref<string | null>(null)

/** Seul jour tabulable : celui qui a le focus, sinon l'arrivée, sinon aujourd'hui, sinon le 1er jour actif visible. */
const tabbableDate = computed(() => {
  const candidates = [focusedDate.value, props.modelValue.start, props.today]
  for (const c of candidates) {
    if (c && isVisible(c) && isSelectable(c)) return c
  }
  const first = visibleMonths.value[0]!
  return first < props.today ? props.today : first
})

function clampSelectable(iso: string): string {
  if (iso < props.today) return props.today
  if (iso > lastSelectable.value) return lastSelectable.value
  return iso
}

function moveFocus(target: string) {
  const next = clampSelectable(target)
  focusedDate.value = next
  // Le mois affiché suit le focus quand il sort des mois visibles.
  if (next < visibleMonths.value[0]!) firstMonth.value = clampFirstMonth(startOfMonth(next))
  else if (next > lastVisibleDay.value) firstMonth.value = clampFirstMonth(addMonths(startOfMonth(next), -(monthCount.value - 1)))
  nextTick(() => {
    rootRef.value?.querySelector<HTMLElement>(`[data-date="${next}"]`)?.focus()
  })
}

function onDayKeydown(event: KeyboardEvent, iso: string) {
  // Alt+flèche (Retour / Suivant du navigateur) et autres raccourcis : laissés au navigateur.
  if (event.altKey || event.ctrlKey || event.metaKey) return
  let target: string | null = null
  switch (event.key) {
    case 'ArrowLeft': target = addDays(iso, -1); break
    case 'ArrowRight': target = addDays(iso, 1); break
    case 'ArrowUp': target = addDays(iso, -7); break
    case 'ArrowDown': target = addDays(iso, 7); break
    case 'Home': target = startOfWeek(iso); break
    case 'End': target = endOfWeek(iso); break
    case 'PageUp': target = addMonths(iso, -1); break
    case 'PageDown': target = addMonths(iso, 1); break
  }
  if (!target) return
  event.preventDefault()
  moveFocus(target)
}

// ─── Sélection ───
function selectDay(iso: string) {
  if (!isSelectable(iso)) return
  focusedDate.value = iso
  emit('update:modelValue', nextDateSelection(props.modelValue, iso))
}

function clear() {
  emit('update:modelValue', { start: null, end: null })
}

type DayState = 'start' | 'end' | 'range' | null

function dayState(iso: string): DayState {
  const { start, end } = props.modelValue
  if (iso === start) return 'start'
  if (iso === end) return 'end'
  if (start && end && iso > start && iso < end) return 'range'
  return null
}

function dayLabel(iso: string): string {
  const parts = [formatDayLong(iso, locale.value, { currentYear: currentYear.value })]
  if (iso === props.today) parts.push(t('pages.home.wizard.dates.calendar.today'))
  const state = dayState(iso)
  if (state === 'start') parts.push(t('pages.home.wizard.dates.calendar.stateArrival'))
  else if (state === 'end') parts.push(t('pages.home.wizard.dates.calendar.stateDeparture'))
  else if (state === 'range') parts.push(t('pages.home.wizard.dates.calendar.stateInRange'))
  return parts.join(', ')
}

function dayClass(iso: string): string {
  if (!isSelectable(iso)) return 'cursor-not-allowed border-transparent text-paper-900/55'
  const state = dayState(iso)
  if (state === 'start' || state === 'end') return 'cursor-pointer border-sage-700 bg-sage-700 font-medium text-paper-50'
  const today = iso === props.today ? 'border-paper-900' : 'border-transparent'
  if (state === 'range') return `cursor-pointer bg-sage-100 text-paper-900 ${today}`
  return `cursor-pointer text-paper-900 hover:bg-paper-200/60 ${today}`
}

function boxValue(iso: string | null): string {
  return iso
    ? formatDayLong(iso, locale.value, { currentYear: currentYear.value })
    : t('pages.home.wizard.dates.calendar.toChoose')
}
</script>

<template>
  <div
    ref="rootRef"
    class="flex min-w-0 flex-col gap-5"
  >
    <!-- Encadrés Arrivée / Départ en toutes lettres -->
    <div
      class="grid grid-cols-2 gap-3"
      aria-live="polite"
    >
      <div class="flex min-w-0 flex-col gap-1 rounded-lg border-[1.5px] border-sage-500 bg-paper-50 px-4 py-3">
        <span class="text-base leading-[1.3] text-paper-900/75">{{ $t('pages.home.wizard.dates.calendar.arrival') }}</span>
        <span
          data-box="start"
          class="text-[19px] font-medium leading-[1.25] break-words"
        >{{ boxValue(modelValue.start) }}</span>
      </div>
      <div class="flex min-w-0 flex-col gap-1 rounded-lg border-[1.5px] border-sage-500 bg-paper-50 px-4 py-3">
        <span class="text-base leading-[1.3] text-paper-900/75">{{ $t('pages.home.wizard.dates.calendar.departure') }}</span>
        <span
          data-box="end"
          class="text-[19px] font-medium leading-[1.25] break-words"
        >{{ boxValue(modelValue.end) }}</span>
      </div>
    </div>

    <!-- Flèches | Effacer les dates | flèche -->
    <div class="flex items-center justify-between gap-3">
      <button
        type="button"
        data-nav="prev"
        class="flex size-12 flex-none cursor-pointer items-center justify-center rounded-lg border-[1.5px] border-sage-500 bg-paper-50 text-paper-900 hover:bg-paper-200/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700 disabled:cursor-not-allowed disabled:opacity-40"
        :aria-label="$t('pages.home.wizard.dates.calendar.prevMonth')"
        :disabled="!canGoPrev"
        @click="shiftMonth(-1)"
      >
        <UIcon
          name="i-lucide-arrow-left"
          class="size-[22px]"
          aria-hidden="true"
        />
      </button>
      <button
        type="button"
        data-action="clear"
        class="min-h-11 cursor-pointer px-1 py-2 text-[17px] text-sage-700 underline underline-offset-[5px] hover:text-sage-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700"
        @click="clear"
      >
        {{ $t('pages.home.wizard.dates.calendar.clear') }}
      </button>
      <button
        type="button"
        data-nav="next"
        class="flex size-12 flex-none cursor-pointer items-center justify-center rounded-lg border-[1.5px] border-sage-500 bg-paper-50 text-paper-900 hover:bg-paper-200/60 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700 disabled:cursor-not-allowed disabled:opacity-40"
        :aria-label="$t('pages.home.wizard.dates.calendar.nextMonth')"
        :disabled="!canGoNext"
        @click="shiftMonth(1)"
      >
        <UIcon
          name="i-lucide-arrow-right"
          class="size-[22px]"
          aria-hidden="true"
        />
      </button>
    </div>

    <!-- Annonce persistante du (des) mois affiché(s) : les <caption> sont recréées à chaque changement de mois -->
    <p
      data-live="months"
      aria-live="polite"
      class="sr-only"
    >
      {{ monthsAnnouncement }}
    </p>

    <!-- Mois -->
    <div
      class="grid min-w-0 gap-6"
      :class="monthCount === 2 ? 'grid-cols-2' : 'grid-cols-1'"
    >
      <table
        v-for="month in months"
        :key="month.id"
        role="grid"
        class="w-full min-w-0 table-fixed border-collapse"
      >
        <caption
          class="pb-3 text-left font-display text-[22px] font-normal leading-[1.2] text-paper-900"
        >
          {{ month.title }}
        </caption>
        <thead>
          <tr>
            <th
              v-for="(name, i) in weekdaysLong"
              :key="name"
              scope="col"
              class="pb-2 text-center text-base font-normal text-paper-900/75"
            >
              <span aria-hidden="true">{{ weekdaysShort[i]!.slice(0, 2) }}</span>
              <span class="sr-only">{{ name }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(week, w) in month.weeks"
            :key="w"
          >
            <td
              v-for="(day, d) in week"
              :key="day ?? `empty-${w}-${d}`"
              class="p-0 py-0.5 text-center"
              :aria-selected="day && (dayState(day) === 'start' || dayState(day) === 'end') ? 'true' : undefined"
            >
              <button
                v-if="day"
                type="button"
                :data-date="day"
                :tabindex="day === tabbableDate ? 0 : -1"
                :disabled="!isSelectable(day)"
                :aria-label="dayLabel(day)"
                class="mx-auto flex h-[46px] w-full max-w-[46px] items-center justify-center rounded-md border-[1.5px] text-lg focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-700 lg:h-11 lg:max-w-11"
                :class="dayClass(day)"
                @click="selectDay(day)"
                @keydown="onDayKeydown($event, day)"
                @focus="focusedDate = day"
              >
                {{ Number(day.slice(8)) }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>
